import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { getUserProfile, updateUserProfile } from '../services/api';
import { UserContextType, KidsUserProfile, Complexity } from '../types';

const UserContext = createContext<UserContextType | undefined>(undefined);

export const useUser = (): UserContextType => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

interface UserProviderProps {
  children: ReactNode;
}

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const { currentUser } = useAuth();
  const [profile, setProfile] = useState<KidsUserProfile | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [needsOnboarding, setNeedsOnboarding] = useState<boolean>(false);

  const refreshProfile = useCallback(async () => {
    if (!currentUser) {
      setProfile(null);
      setNeedsOnboarding(false);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const userProfile = await getUserProfile(currentUser.uid);

      if (!userProfile || !userProfile.age || !userProfile.complexity) {
        setNeedsOnboarding(true);
        setProfile(null);
      } else {
        setNeedsOnboarding(false);
        setProfile(userProfile);
      }
    } catch (error) {
      console.error('Failed to fetch user profile:', error);
      setNeedsOnboarding(true);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    refreshProfile();
  }, [refreshProfile]);

  const setAge = useCallback(async (age: number) => {
    if (!currentUser) throw new Error('User not authenticated');

    const complexity = getDefaultComplexity(age);
    const updatedProfile = await updateUserProfile(currentUser.uid, {
      userId: currentUser.uid,
      email: currentUser.email || '',
      age,
      complexity,
      isKidsAccount: true,
    });

    setProfile(updatedProfile);
    setNeedsOnboarding(false);
  }, [currentUser]);

  const setComplexity = useCallback(async (complexity: Complexity) => {
    if (!currentUser) throw new Error('User not authenticated');
    if (!profile) throw new Error('Profile not loaded');

    const updatedProfile = await updateUserProfile(currentUser.uid, {
      ...profile,
      complexity,
    });

    setProfile(updatedProfile);
  }, [currentUser, profile]);

  const value: UserContextType = {
    profile,
    loading,
    setAge,
    setComplexity,
    refreshProfile,
    needsOnboarding,
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};

// Helper to determine default complexity based on age
function getDefaultComplexity(age: number): Complexity {
  if (age <= 6) return 'basic';
  if (age <= 9) return 'intermediate';
  return 'advanced';
}
